import React, { createContext, useState, useRef, useCallback, useContext, useEffect } from 'react';
import { setup_media_recorder } from '../utils/AudioUtils';
import { AlertContext } from './AlertFlag';
import { handleErrorSentry } from '../utils/constants/Error';

const MicrophoneContext = createContext();
const AudioContext = createContext();

const put_mic_local = (deviceId) => {
  localStorage.setItem("microphone", deviceId);
}

const get_mic_local = () => {
  return localStorage.getItem("microphone");
}

export const AudioProvider = React.memo(({ children }) => {
  const { addAlert } = useContext(AlertContext);
  // Microphone
  const [ selectedMic, setSelectedMic ] = useState(null);
  const [ microphones, setMicrophones ] = useState([]);
  // Audio
  const [ recordingState, setRecordingState ] = useState("mounted");
  const [ recordingTime, setRecordingTime ] = useState(0);
  const recordingTimerRef = useRef(null);

  // Persistent locations
  const audioDeviceRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const audioCodecs = useRef(null);
  const audioBlob = useRef(null);

  const loadPermissionsAndMicrophones = useCallback(async () => {
    // Grab permissions. If not given, then nothing happens
    try {
      await navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
        stream.getTracks().forEach(track => track.stop());
      });
    }
    catch (error) {
      addAlert("Microphone permission denied", "danger");
      return;
    }
    try {
      await navigator.mediaDevices.enumerateDevices().then(devices => {
        // Ensure there is a deviceId. Otherwise, grabbing a stream from an empty mic id will throw overconstrained
        const audio_devices = devices.filter(device => device.kind === "audioinput" && device.deviceId && device.deviceId !== "");
        if (audio_devices && audio_devices.length < 1) {
          addAlert("There are currently no microphones available on this device. Please check your microphone is configured!", "danger");
          return;
        }
        const local_mic_id = get_mic_local();
        const selectedLocalMicExists = audio_devices.some(mic => mic.deviceId === local_mic_id);
        
        if (selectedLocalMicExists) {
          setSelectedMic(local_mic_id);
        } else {
          setSelectedMic(audio_devices[0].deviceId);
        }
        setMicrophones(audio_devices);
        setRecordingState("initialized");
      });
    } catch (error) {
      addAlert(error.message, "danger");
      handleErrorSentry(error, "tryCatch");
    }
  }, []);

  // Swap before recording
  const swapMicrophones = useCallback((deviceId) => {
    const is_acceptable_mic = microphones.some(mic => mic.deviceId === deviceId);
    if (!is_acceptable_mic) {
      addAlert("Microphone is not selectable.", "danger");
      handleErrorSentry(Error("Improper microphone selected"), "generic");
      return;
    }

    setSelectedMic(deviceId);
    put_mic_local(deviceId);
  }, [microphones, recordingState]);

  const startCapture = useCallback(async () => {
    if (recordingState !== "initialized") {
      handleErrorSentry(Error("Recording state is not initialized"), "generic");
      return;
    }

    try {
      // Clean current recording
      audioBlob.current = null;
      recordedChunksRef.current = [];
      if (!selectedMic) {
        addAlert("No microphone selected", "danger");
        handleErrorSentry(Error("No microphone selected"), "generic");
        return;
      }
      // Create source with initial media stream
      const firstAudioStream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: selectedMic, echoCancellation: true }});
      audioDeviceRef.current = firstAudioStream;

      const newMediaRecorder = setup_media_recorder(firstAudioStream, (chunk) => { recordedChunksRef.current.push(chunk) })
      mediaRecorderRef.current = newMediaRecorder["media_recorder"];
      audioCodecs.current = newMediaRecorder["audio_codecs"];

      mediaRecorderRef.current.start(5000); // Collect data every second
      setRecordingState('recording');
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      // Cleanup if partially initialized
      cleanRecordingStates();
      cleanAudio();
      addAlert("Couldn't begin recording", "danger");
      handleErrorSentry(error, "tryCatch");
    }
  }, [recordingState, selectedMic]);


  const pauseCapture = useCallback(() => {
    if (recordingState !== "recording") {
      handleErrorSentry(Error("Cannot pause recording as recording has not started"), "generic");
    }

    try {
      mediaRecorderRef.current.pause();
      clearInterval(recordingTimerRef.current);
      setRecordingState('paused');
    }
    catch(error) {
      handleErrorSentry(error, "tryCatch");
    }
  }, [recordingState])

  // CHECK IF STREAMS ARE ALIVE AND IF THE RECORDING CAN BE CONTINUED. IF NOT, THEN A FLAG SHOULD BE TRIGGERED TO FORCE USER TO SUBMIT
  const unpauseCapture = useCallback(() => {
    if (recordingState !== "paused") {
      handleErrorSentry(Error("Cannot play recording as recording is not paused"), "generic");
    }

    try {
      mediaRecorderRef.current.resume();
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      setRecordingState('recording');
    } catch (error) {
      handleErrorSentry(error, "tryCatch");
    }
  }, [recordingState]);

  const stopCapture = useCallback(() => {
    if (recordingState !== "recording" && recordingState !== "paused") {
      handleErrorSentry(Error("Cannot stop recording as recording is not recording or paused"), "generic");
    }

    try {
      return new Promise((resolve, reject) => {
        mediaRecorderRef.current.onstop = () => {
          setTimeout(() => {
            let blob;
            if (audioCodecs.current) {
              blob = new Blob(recordedChunksRef.current, {
                type: audioCodecs.current
              });
            } else {
              blob = new Blob(recordedChunksRef.current);
            }
            resolve(blob);
  
            cleanAudio();
            cleanRecordingStates();

            audioBlob.current = blob;
          }, 0);
        };
        mediaRecorderRef.current.onerror = (err) => {
          reject(err);
        };
        // This fires final ondataavailable events and triggers `onstop`
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current = null;
      });
    } catch (error) {
      handleErrorSentry(error, "tryCatch");
    }
  }, [recordingState])

  const retryBlob = useCallback(() => {
    if (recordingState === "ended" && audioBlob.current) {
      return audioBlob.current;
    }
  }, [recordingState])

  // Cleanup audio controls and connections
  const cleanAudio = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    mediaRecorderRef.current = null;

    if (audioDeviceRef.current) {
      audioDeviceRef.current.getTracks().forEach((track) => track.stop());
      audioDeviceRef.current = null;
    }
  }, [])

  const cleanRecordingStates = useCallback(() => {
    setRecordingTime(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    recordedChunksRef.current = [];
    setRecordingState("ended");
  }, [])

  const restart = useCallback(() => {
    if (selectedMic && microphones.length > 0 && recordingState === "ended") {
      audioBlob.current = null;
      setRecordingState("initialized")
    } else {
      loadPermissionsAndMicrophones();
    }
  }, [recordingState, microphones, selectedMic])

  useEffect(() => {
    return () => {
      cleanAudio();
      cleanRecordingStates();
    }
  }, [])

  // Exposed contexts
  const microphoneValue = {
    microphones,
    selectedMic,
    loadPermissionsAndMicrophones,
    swapMicrophones,
  };
  
  // Recorder context value
  const recorderValue = {
    recordingState,
    recordingTime,
    audioDeviceRef,
    audioCodecs,
    retryBlob,
    startCapture,
    pauseCapture,
    unpauseCapture,
    stopCapture,
    restart,
    cleanAudio,
    cleanRecordingStates,
  };
  
  return (
    <MicrophoneContext.Provider value={microphoneValue}>
      <AudioContext.Provider value={recorderValue}>
        {children}
      </AudioContext.Provider>
    </MicrophoneContext.Provider>
  );
})

export const useMicrophone = () => useContext(MicrophoneContext);
export const useRecorder = () => useContext(AudioContext);

export { MicrophoneContext, AudioContext };


